import { Input, Col, Button, Row } from "reactstrap";
import FormError from "./FormError";
import React, { forwardRef, useState, useEffect, useImperativeHandle, useRef } from "react";
import { FormikProps } from "formik";
import { useLazyQuery, useMutation } from "@apollo/client";
import { getVideoUploadURLQuery } from "../../../graphql/queries/getVideoUploadURL";
import Loading from "../../Loading";
import VideoLibrarySelectorModal from "../../ContentManagement/VideoLibrarySelectorModal";
import { getVideoUrlFromCloudflareId } from "../../../helper/getVideoUrlFromCloudflareId";
import { executeDeleteVideo } from "../../../graphql/queries/getImageUploadURL";

// TODO : ISSUE it seems using this on formik the lazy query is running but itself when multiple items of this components are used so for now 
// we will have an option to not trigger the lazy query for getting the url and do it outside of this component

interface Props {
    isDisabled?: boolean;
    formikRef: React.RefObject<FormikProps<any>>;
    extensionAllowed?: string;
    transferFunction: (url: string, cloudflareId: string, blob: File) => void;
    isGettingUrl: (isGetting: boolean) => void;
    videoName: string;
    videoUrlName?: string;
    formGroupClassName?: string;
    required?: boolean;
    dontFetchUrl?: boolean;
    label?: string;
    maxMb?: number;
}

const VideoUploadInput = forwardRef(function VideoUploadInput(
    { formikRef, extensionAllowed, isDisabled, transferFunction, isGettingUrl,
        videoName, videoUrlName, formGroupClassName, required, dontFetchUrl = false, label, maxMb = 100 }: Props,
    ref: React.Ref<{ clearVideoField: () => void, refetchUrl?: () => void }>
) {
    const fileInputRef = useRef<any>(null);
    const [videoTypesAllowed, setVideoTypesAllowed] = useState<string>("video/*");

    const [previewVideo, setPreviewVideo] = useState<string>(null);
    const [blobFile, setBlobFile] = useState<File>(null);

    const { values, setFieldValue, touched, errors } = formikRef?.current || {};
    const [validationMessage, setValidationMessage] = useState("");

    const [isVideoSelectorOpen, setIsVideoSelectorOpen] = useState<boolean>(false);
    const [isFetchingVideo, setIsFetchingVideo] = useState<boolean>(false);

    const [videoFileId, setVideoFileId] = useState<string>(null);

    const [
        getSignedUrl,
        { loading: fetchingSignedUrl, error: fetchSignedUrlError },
    ] = useLazyQuery(getVideoUploadURLQuery, {
        fetchPolicy: "network-only",
        onCompleted({ getVideoUploadURL }) {
            transferFunction(getVideoUploadURL.uploadURL, getVideoUploadURL.cloudflareId, blobFile);
            setIsFetchingVideo(false);
            setVideoFileId(getVideoUploadURL.cloudflareId);
        },
        onError() {
            console.error('error on getting signed video url');
        }
    });

    const [deleteVideo] = useMutation(executeDeleteVideo);

    const fetchingSignedUrlBefore = useRef(false);
    useEffect(() => {
        if (fetchingSignedUrlBefore.current !== fetchingSignedUrl) {
            fetchingSignedUrlBefore.current = fetchingSignedUrl;
            isGettingUrl(fetchingSignedUrl);
        }
    }, [fetchingSignedUrl, isGettingUrl]);

    useEffect(() => {
        if (extensionAllowed) {
            setVideoTypesAllowed(extensionAllowed);
        }
    }, [extensionAllowed]);

    // Function to expose to the parent
    const clearVideoField = () => {
        setPreviewVideo(null);
        setBlobFile(null);
        transferFunction(null, null, null);
    };

    const refetchUrl = () => {
        if (blobFile) {
            getSignedUrl({
                variables: {
                    fileName: blobFile.name,
                    fileSize: blobFile.size,
                },
            });
        }
    };

    // Expose the child function to the parent via ref
    useImperativeHandle(ref, () => ({
        clearVideoField: clearVideoField,
        refetchUrl: refetchUrl
    }));

    const [reseter, setReseter] = useState<boolean>(false);
    const HandleRemoveVideo = async () => {
        
        if(videoFileId) {
            setIsFetchingVideo(true);
            await deleteVideo({
                variables: {
                    fileId: videoFileId,
                },
            });
            setIsFetchingVideo(false);
        }
        setReseter(!reseter);
        setPreviewVideo(null);
        setBlobFile(null);
        setFieldValue(videoUrlName, "");
        setFieldValue(videoName, "");
        transferFunction(null, null, null);
        setValidationMessage("");
    };

    const ToggleVideoSelector = () => {
        setIsVideoSelectorOpen(!isVideoSelectorOpen);
    };

    const GetSelectedVideo = async (id: string) => {
        // For video library selection, we just set the video_link field
        // The actual video URL will be handled by the parent component

        const videoWithUrl = id.includes('https://') || id.includes('http://') ? id : getVideoUrlFromCloudflareId(id);
        setFieldValue(videoUrlName || "video_url", videoWithUrl);

        setFieldValue(videoName, videoWithUrl);
        setValidationMessage("");
        setPreviewVideo(null);
        setBlobFile(null);
        transferFunction(null, null, null);
    };

    return (
        <div className={`form-group ${formGroupClassName}`} ref={fileInputRef}>
            <label>{label || "Video"} {required ? "*" : ""}</label>
            {
                (!previewVideo && !values?.[videoUrlName]) &&
                <Input
                    name={videoName}
                    type="file"
                    accept={videoTypesAllowed}
                    className={`form-control`}
                    disabled={fetchingSignedUrl}
                    onChange={(event) => {
                        // Use Formik's setFieldValue to set the file value
                        const file = event.currentTarget.files[0];
                        if (file) {
                            const videoUrl = URL.createObjectURL(file);

                            setFieldValue(videoName, file.name);
                            setValidationMessage("");

                            // Check file type
                            if (!file.type.startsWith('video/')) {
                                setValidationMessage("Invalid file type. Please select a video file.");
                                return;
                            }

                            // Check file size (in MB)
                            const fileSizeMB = file.size / (1024 * 1024);
                            if (fileSizeMB > maxMb) {
                                setValidationMessage(`File size must be less than ${maxMb}MB`);
                                return;
                            }

                            setPreviewVideo(videoUrl);
                            setBlobFile(file);
                            if (!dontFetchUrl) {
                                setIsFetchingVideo(true);
                                getSignedUrl({
                                    variables: {
                                        fileName: file.name,
                                        fileSize: file.size,
                                    },
                                });
                            } else {
                                transferFunction(null, null, file);
                            }
                        } else {
                            setFieldValue(videoName, null);
                            setPreviewVideo(null);
                            setBlobFile(null);
                            transferFunction(null, null, null);
                        }
                    }}
                />
            }
            {
                validationMessage &&
                <FormError>{validationMessage}</FormError>
            }
            
            {
            touched?.[videoName] && typeof errors?.[videoName] === 'string' && (errors?.[videoName] as string) &&
            <FormError>
                {touched?.[videoName] && typeof errors?.[videoName] === 'string' && (errors?.[videoName] as string)}
                {touched?.[videoUrlName] && typeof errors?.[videoUrlName] === 'string' && (errors?.[videoUrlName] as string)}
            </FormError>}
            {fetchSignedUrlError && (
                <FormError>Error on setting up for video upload</FormError>
            )}
            {
                (fetchingSignedUrl || isFetchingVideo) ?
                    <Loading />
                    :
                    <Row>
                        <Col md={10}>
                            {
                                (previewVideo || values?.[videoUrlName]) &&
                                <div>
                                    {previewVideo ? (
                                        <video
                                            className="pt-3"
                                            style={{
                                                width: "100%",
                                                borderRadius: "0.375rem",
                                                marginBottom: "1rem",
                                            }}
                                            controls
                                            src={previewVideo}
                                        />
                                    ) : values?.[videoUrlName] ? (
                                        <div className="pt-3">
                                            <a
                                                target="_blank"
                                                href={values[videoUrlName]}
                                                rel="noopener noreferrer"
                                                className="w-100 pt-2 d-block text-truncate"
                                            >
                                                {values[videoUrlName]}
                                            </a>
                                        </div>
                                    ) : null}
                                </div>
                            }
                        </Col>
                        <Col className="d-flex align-items-center" md={2}>
                            {
                                (previewVideo || values?.[videoUrlName]) &&
                                (
                                    <Button
                                        title="Click to remove this video and to upload new video."
                                        color="secondary"
                                        type="button"
                                        disabled={isDisabled || isFetchingVideo}
                                        onClick={HandleRemoveVideo}
                                    >
                                        <div>
                                            <i className="fas fa-trash-alt"></i>
                                        </div>
                                    </Button>
                                )
                            }
                        </Col>
                    </Row>
            }
            {
                (!previewVideo && !values?.[videoUrlName] && !isFetchingVideo) &&
                <span className="text-primary" role="button" onClick={ToggleVideoSelector}>
                    Select From Library
                </span>
            }

            <VideoLibrarySelectorModal
                getSelected={GetSelectedVideo}
                modalOpen={isVideoSelectorOpen}
                toggleModal={ToggleVideoSelector}
            />
        </div>
    )
});

export default VideoUploadInput;